import { logger } from '@/lib/logger';

// ---------------------------------------------------------------------------
// TypeScript declarations for the global grecaptcha object (reCAPTCHA v3)
// ---------------------------------------------------------------------------

interface GRecaptchaV3 {
  ready: (callback: () => void) => void;
  execute: (siteKey: string, options: { action: string }) => Promise<string>;
}

declare global {
  interface Window {
    grecaptcha: GRecaptchaV3;
  }
}

// ---------------------------------------------------------------------------
// Site key from environment
// ---------------------------------------------------------------------------

export const RECAPTCHA_SITE_KEY =
  process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY ?? '';

// ---------------------------------------------------------------------------
// Script loader
// ---------------------------------------------------------------------------

let loadPromise: Promise<void> | null = null;

/**
 * Dynamically loads the reCAPTCHA v3 script if it has not been loaded yet.
 * Resolves once `grecaptcha.ready` fires.
 */
export function loadRecaptchaScript(
  siteKey: string = RECAPTCHA_SITE_KEY,
): Promise<void> {
  if (loadPromise) return loadPromise;

  loadPromise = new Promise<void>((resolve, reject) => {
    // If the script tag already exists (e.g. added by another module), skip injection
    if (typeof window !== 'undefined' && window.grecaptcha?.ready) {
      window.grecaptcha.ready(() => resolve());
      return;
    }

    if (typeof document === 'undefined') {
      reject(new Error('reCAPTCHA can only be loaded in a browser environment'));
      return;
    }

    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      window.grecaptcha.ready(() => resolve());
    };

    script.onerror = () => {
      loadPromise = null; // allow retry
      const error = new Error('Failed to load reCAPTCHA script');
      logger.error('Failed to load reCAPTCHA script', error, {
        feature: 'recaptcha',
        route: 'loadRecaptchaScript',
      });
      reject(error);
    };

    document.head.appendChild(script);
  });

  return loadPromise;
}

// ---------------------------------------------------------------------------
// Token execution
// ---------------------------------------------------------------------------

/**
 * Executes reCAPTCHA v3 for the given action and returns a token string.
 * Automatically loads the script if it has not been loaded yet.
 */
export async function executeRecaptcha(action: string): Promise<string> {
  const siteKey = RECAPTCHA_SITE_KEY;

  if (!siteKey) {
    logger.warn('reCAPTCHA site key is not configured', {
      feature: 'recaptcha',
      route: 'executeRecaptcha',
    });
    return '';
  }

  await loadRecaptchaScript(siteKey);

  return window.grecaptcha.execute(siteKey, { action });
}

// ---------------------------------------------------------------------------
// Header helper
// ---------------------------------------------------------------------------

/**
 * Returns a header object containing the reCAPTCHA response token, suitable
 * for spreading into an Axios request config. Returns `undefined` when no
 * token is provided so callers can safely spread without adding empty headers.
 */
export function makeRecaptchaHeader(
  token?: string,
): Record<string, string> | undefined {
  if (!token) return undefined;

  return { 'g-recaptcha-response': token };
}
